import React, { createContext, useState, useEffect, useContext } from 'react';
import { loadConfigFromFile, autoSaveConfig, saveConfigToFile, uploadConfigFile } from '../utils/configManager';

// 기본 서버 설정 목록
const defaultServers = [
  {
    id: 'rank-api-server',
    name: '거래대금 순위 API 서버',
    baseUrl: 'http://localhost:8080',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true
  },
  {
    id: 'board-api-server',
    name: '게시판 API 서버',
    baseUrl: 'http://localhost:9001',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '게시판, 게시글, 댓글 관리 API 서버'
  },
  {
    id: 'auth-api-server',
    name: '인증 API 서버',
    baseUrl: 'http://localhost:9000',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: true,
    description: '로그인, 회원가입, 사용자 관리 API 서버'
  },
  {
    id: 'news-server',
    name: '뉴스 데이터 서버',
    baseUrl: 'http://localhost:8081',
    domain: 'localhost',
    apiTimeout: 10000,
    isActive: false
  },
  {
    id: 'chart-api-server',
    name: '차트 API 서버',
    baseUrl: 'http://localhost:8082',
    domain: 'localhost',
    apiTimeout: 15000,
    isActive: true
  }
];

// 기본 거래대금 표 설정
const defaultTableSettings = {
  stockNameFilter: {
    enabled: false,
    keywords: [],
    mode: 'exclude',
    caseSensitive: false
  },
  themeFilter: {
    enabled: false,
    keywords: [],
    mode: 'exclude',
    caseSensitive: false
  },
  visibleColumns: {
    rank: true,
    name: true,
    price: true,
    rate: true,
    tradingValue: true,
    volume: true,
    marketCap: true,
    theme: true
  },
  themeDisplay: {
    showAll: true,
    maxThemes: 3,
    separator: ', '
  },
  numberFormat: {
    useComma: true,
    abbreviateVolume: true,
    abbreviateMarketCap: true,
    decimalPlaces: 2
  },
  colorScheme: {
    positiveRate: '#d32f2f',
    negativeRate: '#1976d2',
    neutralRate: '#666666'
  }
};

// Context 생성
export const ServerConfigContext = createContext();

// Provider 컴포넌트
export const ServerConfigProvider = ({ children }) => {
  const [servers, setServers] = useState(defaultServers);
  const [tableSettings, setTableSettings] = useState(defaultTableSettings);
  const [menuPermissions, setMenuPermissions] = useState(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  // 컴포넌트 마운트 시 설정 파일에서 설정 불러오기
  useEffect(() => {
    const initializeConfigs = async () => {
      try {
        console.log('🚀 설정 초기화 시작...');
        
        let serverConfig = defaultServers;
        let tableConfig = defaultTableSettings;
        let isLoaded = false;
        
        // 1단계: 로컬 스토리지에서 먼저 확인 (동기적으로)
        const savedConfigs = localStorage.getItem('appConfigs');
        console.log('🔍 [ServerConfigContext] 로컬 스토리지 확인:', { 
          hasSavedConfigs: !!savedConfigs,
          savedConfigsLength: savedConfigs?.length || 0 
        });
        
        if (savedConfigs) {
          try {
            const parsedConfigs = JSON.parse(savedConfigs);
            console.log('🔍 [ServerConfigContext] 파싱된 설정:', {
              hasServers: !!parsedConfigs.servers,
              serversCount: parsedConfigs.servers?.length || 0,
              serverIds: parsedConfigs.servers?.map(s => s.id) || []
            });
            
            if (parsedConfigs.servers && parsedConfigs.servers.length > 0) {
              console.log('✅ 로컬 스토리지에서 설정 로드 완료');
              serverConfig = parsedConfigs.servers;
              if (parsedConfigs.tableSettings) {
                tableConfig = { ...defaultTableSettings, ...parsedConfigs.tableSettings };
              }
              isLoaded = true;
            }
          } catch (error) {
            console.warn('❌ 로컬 스토리지 파싱 오류:', error);
          }
        }
        
        if (!isLoaded) {
          // 2단계: 로컬 스토리지가 없으면 기본 설정으로 초기화
          console.log('📋 기본 설정으로 초기 설정:', {
            defaultServersCount: defaultServers.length,
            defaultServerIds: defaultServers.map(s => s.id)
          });
          serverConfig = defaultServers;
          tableConfig = defaultTableSettings;
        }
        
        // 상태를 일괄적으로 업데이트
        console.log('🔧 [ServerConfigContext] 서버 설정 업데이트:', {
          serversCount: serverConfig.length,
          serverIds: serverConfig.map(s => s.id)
        });
        
        setServers(serverConfig);
        setTableSettings(tableConfig);
        
        // 모든 상태 업데이트 완료 후 설정 로드 완료 표시
        setTimeout(() => {
          setIsConfigLoaded(true);
          console.log('✅ 설정 초기화 완료 - isConfigLoaded: true');
        }, 50);
        
        // 3단계: 백그라운드에서 설정 파일 로드 시도 (선택적)
        setTimeout(async () => {
          try {
            const fileResult = await loadConfigFromFile();
            if (fileResult.success && fileResult.data) {
              console.log('✅ 파일에서 설정 로드 성공 (백그라운드)');
              let updatedServers = serverConfig;
              let updatedTableSettings = tableConfig;
              
              if (fileResult.data.servers && fileResult.data.servers.length > 0) {
                updatedServers = fileResult.data.servers;
                setServers(updatedServers);
                console.log('📋 파일에서 서버 설정 업데이트:', updatedServers.map(s => s.id));
              }
              if (fileResult.data.tableSettings) {
                updatedTableSettings = { ...defaultTableSettings, ...fileResult.data.tableSettings };
                setTableSettings(updatedTableSettings);
              }
              if (fileResult.data.menuPermissions) {
                setMenuPermissions(fileResult.data.menuPermissions);
                console.log('📋 파일에서 메뉴 권한 설정 로드:', fileResult.data.menuPermissions);
              }
              
              // 파일에서 로드한 설정을 localStorage에도 저장
              const fileBasedConfig = {
                servers: updatedServers,
                tableSettings: updatedTableSettings,
                version: '1.0'
              };
              localStorage.setItem('appConfigs', JSON.stringify(fileBasedConfig));
              console.log('💾 파일 기반 설정을 localStorage에 동기화 완료');
            }
          } catch (error) {
            console.warn('⚠️ 백그라운드 설정 로드 실패 (기본 설정 유지):', error);
          }
        }, 200);
        
      } catch (error) {
        console.error('❌ 설정 초기화 중 오류가 발생했습니다:', error);
        // 오류 발생 시에도 기본값 설정 후 로드 완료 처리
        setServers(defaultServers);
        setTableSettings(defaultTableSettings);
        setTimeout(() => {
          setIsConfigLoaded(true);
          console.log('✅ 오류 복구 후 설정 초기화 완료 - isConfigLoaded: true');
        }, 50);
      }
    };
    
    initializeConfigs();
  }, []);

  // 통합 설정 저장 함수 (파일 + 로컬 스토리지 동시 저장)
  const saveAppConfigs = async (serverConfigs = servers, tableConfigs = tableSettings, menuPerms = null) => {
    try {
      // menuPerms가 명시적으로 전달되지 않으면 현재 menuPermissions 상태 사용
      const currentMenuPermissions = menuPerms !== null ? menuPerms : menuPermissions;

      const appConfigs = {
        servers: serverConfigs,
        tableSettings: tableConfigs,
        ...(currentMenuPermissions && { menuPermissions: currentMenuPermissions }),
        version: '1.0',
        timestamp: new Date().toISOString()
      };
      
      console.log('💾 [ServerConfigContext] 설정 저장 시도:', {
        serversCount: serverConfigs.length,
        serverIds: serverConfigs.map(s => s.id),
        hasMenuPermissions: !!currentMenuPermissions,
        menuPermissions: currentMenuPermissions,
        configKeys: Object.keys(appConfigs),
        timestamp: appConfigs.timestamp
      });
      
      // 로컬 스토리지에 저장 (메인 설정)
      localStorage.setItem('appConfigs', JSON.stringify(appConfigs));
      console.log('✅ localStorage (appConfigs) 저장 완료');
      
      // 파일 시스템 + 서버 파일 저장 (비동기)
      try {
        const autoSaveResult = await autoSaveConfig(appConfigs);
        if (autoSaveResult) {
          console.log('✅ 서버 파일 + 백업 저장 완료');
        } else {
          console.warn('⚠️ 서버 파일 저장 실패, 로컬 백업만 유지');
        }
      } catch (saveError) {
        console.warn('⚠️ 자동 저장 중 오류:', saveError);
      }
      
    } catch (error) {
      console.error('❌ 설정 저장 중 오류:', error);
      // 저장 실패 시에도 사용자에게 알리기
      alert(`설정 저장에 실패했습니다: ${error.message}`);
    }
  };

  // 거래대금 표 설정 업데이트 함수
  const updateTableSettings = async (newSettings) => {
    const updatedSettings = typeof newSettings === 'function'
      ? newSettings(tableSettings)
      : { ...tableSettings, ...newSettings };

    setTableSettings(updatedSettings);
    await saveAppConfigs(servers, updatedSettings);
  };

  // 서버 추가 함수
  const addServer = async (serverData) => {
    try {
      // 유효성 검사
      if (!serverData.name || !serverData.baseUrl || !serverData.domain) {
        throw new Error('필수 설정값이 누락되었습니다.');
      }

      // URL 형식 검사
      try {
        new URL(serverData.baseUrl);
      } catch {
        throw new Error('올바른 URL 형식이 아닙니다.');
      }

      // 타임아웃 값 검사
      if (serverData.apiTimeout < 1000 || serverData.apiTimeout > 60000) {
        throw new Error('타임아웃은 1초에서 60초 사이여야 합니다.');
      }

      // ID 중복 검사
      const existingServer = servers.find(s => s.id === serverData.id);
      if (existingServer) {
        throw new Error('같은 ID의 서버가 이미 존재합니다.');
      }

      const newServer = {
        ...serverData,
        id: serverData.id || `server-${Date.now()}`,
        isActive: serverData.isActive || false
      };

      const updatedServers = [...servers, newServer];
      setServers(updatedServers);
      await saveAppConfigs(updatedServers, tableSettings);
      
      return { success: true, message: '서버가 성공적으로 추가되었습니다.' };
    } catch (error) {
      console.error('서버 추가 중 오류:', error);
      return { success: false, message: error.message };
    }
  };

  // 서버 업데이트 함수
  const updateServer = async (serverId, serverData) => {
    try {
      const serverIndex = servers.findIndex(s => s.id === serverId);
      if (serverIndex === -1) {
        throw new Error('서버를 찾을 수 없습니다.');
      }

      // 유효성 검사
      if (!serverData.name || !serverData.baseUrl || !serverData.domain) {
        throw new Error('필수 설정값이 누락되었습니다.');
      }

      try {
        new URL(serverData.baseUrl);
      } catch {
        throw new Error('올바른 URL 형식이 아닙니다.');
      }

      if (serverData.apiTimeout < 1000 || serverData.apiTimeout > 60000) {
        throw new Error('타임아웃은 1초에서 60초 사이여야 합니다.');
      }

      const updatedServers = [...servers];
      updatedServers[serverIndex] = { ...updatedServers[serverIndex], ...serverData };
      setServers(updatedServers);
      await saveAppConfigs(updatedServers, tableSettings);
      
      return { success: true, message: '서버 설정이 성공적으로 업데이트되었습니다.' };
    } catch (error) {
      console.error('서버 업데이트 중 오류:', error);
      return { success: false, message: error.message };
    }
  };

  // 서버 삭제 함수
  const deleteServer = async (serverId) => {
    try {
      const updatedServers = servers.filter(s => s.id !== serverId);
      if (updatedServers.length === servers.length) {
        throw new Error('삭제할 서버를 찾을 수 없습니다.');
      }
      
      setServers(updatedServers);
      await saveAppConfigs(updatedServers, tableSettings);
      
      return { success: true, message: '서버가 성공적으로 삭제되었습니다.' };
    } catch (error) {
      console.error('서버 삭제 중 오류:', error);
      return { success: false, message: error.message };
    }
  };

  // 서버 활성화/비활성화 토글
  const toggleServerActive = async (serverId) => {
    const updatedServers = servers.map(server =>
      server.id === serverId ? { ...server, isActive: !server.isActive } : server
    );
    setServers(updatedServers);
    await saveAppConfigs(updatedServers, tableSettings);
  };

  // 특정 서버 조회
  const getServer = (serverId) => {
    console.log('🔍 [ServerConfigContext] getServer 호출:', {
      serverId,
      serversCount: servers.length,
      availableServerIds: servers.map(s => s.id)
    });
    
    const foundServer = servers.find(server => server.id === serverId);
    console.log('🔍 [ServerConfigContext] 검색 결과:', foundServer);
    
    return foundServer;
  };

  // 활성 서버 목록 조회
  const getActiveServers = () => {
    return servers.filter(server => server.isActive);
  };

  // 설정 초기화 함수
  const resetServerConfigs = () => {
    setServers(defaultServers);
    saveAppConfigs(defaultServers, defaultTableSettings);
  };

  // 통합 설정을 JSON 파일로 내보내기 (개선된 버전)
  const exportAppConfigs = () => {
    const configData = {
      servers: servers,
      tableSettings: tableSettings
    };
    
    return saveConfigToFile(configData);
  };

  // JSON 파일에서 통합 설정 가져오기 (파일 업로드)
  const importAppConfigs = async (file) => {
    try {
      const result = await uploadConfigFile(file);
      
      if (result.success && result.data) {
        // 서버 설정 적용
        setServers(result.data.servers);
        
        // 테이블 설정이 있으면 적용, 없으면 기존 설정 유지
        let newTableSettings = tableSettings;
        if (result.data.tableSettings) {
          newTableSettings = { ...defaultTableSettings, ...result.data.tableSettings };
          setTableSettings(newTableSettings);
        }
        
        saveAppConfigs(result.data.servers, newTableSettings);
        
        const serverCount = result.data.servers.length;
        const hasTableSettings = !!result.data.tableSettings;
        const message = hasTableSettings 
          ? `${serverCount}개의 서버 설정과 거래대금 표 설정을 성공적으로 가져왔습니다.`
          : `${serverCount}개의 서버 설정을 성공적으로 가져왔습니다.`;
        
        return { success: true, message };
      } else {
        return result;
      }
    } catch (error) {
      console.error('설정 가져오기 중 오류:', error);
      return { success: false, message: `설정 가져오기 중 오류: ${error.message}` };
    }
  };

  // 거래대금 표 설정 관련 함수들
  const addFilterKeyword = (keyword) => {
    if (!keyword || typeof keyword !== 'string') return;
    
    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword && !tableSettings.stockNameFilter.keywords.includes(trimmedKeyword)) {
      updateTableSettings({
        stockNameFilter: {
          ...tableSettings.stockNameFilter,
          keywords: [...tableSettings.stockNameFilter.keywords, trimmedKeyword]
        }
      });
    }
  };

  const removeFilterKeyword = (keyword) => {
    updateTableSettings({
      stockNameFilter: {
        ...tableSettings.stockNameFilter,
        keywords: tableSettings.stockNameFilter.keywords.filter(k => k !== keyword)
      }
    });
  };

  const clearFilterKeywords = () => {
    updateTableSettings({
      stockNameFilter: {
        ...tableSettings.stockNameFilter,
        keywords: []
      }
    });
  };

  // 테마 필터 관련 함수들
  const addThemeFilterKeyword = (keyword) => {
    if (!keyword || typeof keyword !== 'string') return;
    
    const trimmedKeyword = keyword.trim();
    if (trimmedKeyword && !tableSettings.themeFilter.keywords.includes(trimmedKeyword)) {
      updateTableSettings({
        themeFilter: {
          ...tableSettings.themeFilter,
          keywords: [...tableSettings.themeFilter.keywords, trimmedKeyword]
        }
      });
    }
  };

  const removeThemeFilterKeyword = (keyword) => {
    updateTableSettings({
      themeFilter: {
        ...tableSettings.themeFilter,
        keywords: tableSettings.themeFilter.keywords.filter(k => k !== keyword)
      }
    });
  };

  const clearThemeFilterKeywords = () => {
    updateTableSettings({
      themeFilter: {
        ...tableSettings.themeFilter,
        keywords: []
      }
    });
  };

  const resetTableSettings = () => {
    setTableSettings(defaultTableSettings);
    saveAppConfigs(servers, defaultTableSettings);
  };

  // 데이터 필터링 함수 (종목명 기준)
  const filterStockData = (data) => {
    if (!tableSettings.stockNameFilter.enabled || tableSettings.stockNameFilter.keywords.length === 0) {
      return data;
    }

    return data.filter(item => {
      const stockName = item.name || item.hname || '';
      const keywords = tableSettings.stockNameFilter.keywords;
      const caseSensitive = tableSettings.stockNameFilter.caseSensitive;
      
      const nameToCheck = caseSensitive ? stockName : stockName.toLowerCase();
      
      const hasMatchingKeyword = keywords.some(keyword => {
        const keywordToCheck = caseSensitive ? keyword : keyword.toLowerCase();
        return nameToCheck.includes(keywordToCheck);
      });

      // include 모드: 키워드가 포함된 것만 표시
      // exclude 모드: 키워드가 포함되지 않은 것만 표시
      return tableSettings.stockNameFilter.mode === 'include' ? hasMatchingKeyword : !hasMatchingKeyword;
    });
  };

  // API 엔드포인트 생성 헬퍼 함수
  const getApiUrl = (serverId, endpoint) => {
    const server = getServer(serverId);
    if (!server) {
      throw new Error(`서버를 찾을 수 없습니다: ${serverId}`);
    }

    // 통합 프록시 패턴 사용: /auth-api 또는 /board-api
    const prefix = serverId === 'auth-api-server' ? '/auth-api' : '/board-api';
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${prefix}/api/v1${cleanEndpoint}`;
  };

  // 스마트 API URL 생성 함수 - 엔드포인트에 따라 자동으로 서버 선택
  const getSmartApiUrl = (endpoint) => {
    console.log(`🚀 [getSmartApiUrl] 호출됨! endpoint: ${endpoint}`);

    // 엔드포인트에 따라 프록시 prefix 결정
    const isAuthRequest = endpoint.includes('/auth/');
    const prefix = isAuthRequest ? '/auth-api' : '/board-api';

    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const finalUrl = `${prefix}/api/v1${cleanEndpoint}`;

    console.log(`🔗 [getSmartApiUrl] 최종 URL: ${endpoint} → ${finalUrl}`);
    return finalUrl;
  };

  // 통합 설정 저장 함수 (app-config.json 호환)
  const saveConfig = async (configData) => {
    try {
      console.log('💾 [ServerConfigContext] 설정 저장:', configData);

      // 로컬 상태 업데이트
      if (configData.servers) {
        setServers(configData.servers);
      }
      if (configData.tableSettings) {
        setTableSettings(configData.tableSettings);
      }
      if (configData.menuPermissions) {
        setMenuPermissions(configData.menuPermissions);
      }

      // app-config.json 파일에 저장 (실제 파일 업데이트)
      try {
        console.log('📡 [ServerConfigContext] API 호출로 app-config.json 저장 시도');
        const response = await fetch('/api/save-config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(configData)
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ [ServerConfigContext] app-config.json 파일 저장 완료:', result);
        } else {
          const errorResult = await response.json();
          console.error('❌ [ServerConfigContext] 파일 저장 API 오류:', errorResult);
          throw new Error(errorResult.message || `HTTP ${response.status}`);
        }
      } catch (fileError) {
        console.warn('⚠️ [ServerConfigContext] 파일 저장 실패:', fileError.message);
        // 파일 저장 실패해도 계속 진행 (localStorage 저장은 수행)
      }

      // localStorage에도 저장
      await saveAppConfigs(
        configData.servers || servers,
        configData.tableSettings || tableSettings,
        configData.menuPermissions || menuPermissions
      );

      console.log('✅ [ServerConfigContext] 설정 저장 완료');
      return { success: true };
    } catch (error) {
      console.error('❌ [ServerConfigContext] 설정 저장 실패:', error);
      throw error;
    }
  };

  // 현재 전체 설정을 하나의 객체로 반환
  const config = {
    servers,
    tableSettings,
    menuPermissions
  };

  // Context 값
  const contextValue = {
    // 통합 설정
    config,
    saveConfig,
    isConfigLoaded,

    // 서버 설정 관련
    servers,
    addServer,
    updateServer,
    deleteServer,
    toggleServerActive,
    getServer,
    getActiveServers,
    resetServerConfigs,
    getApiUrl,
    getSmartApiUrl,

    // 거래대금 표 설정 관련
    tableSettings,
    updateTableSettings,
    addFilterKeyword,
    removeFilterKeyword,
    clearFilterKeywords,
    addThemeFilterKeyword,
    removeThemeFilterKeyword,
    clearThemeFilterKeywords,
    resetTableSettings,
    filterStockData,

    // 통합 설정 관리
    exportAppConfigs,
    importAppConfigs,

    // 편의 기능들
    hasActiveServers: servers.some(s => s.isActive),
    defaultServers,
    defaultTableSettings
  };

  return (
    <ServerConfigContext.Provider value={contextValue}>
      {children}
    </ServerConfigContext.Provider>
  );
};

// useServerConfig 커스텀 훅
export const useServerConfig = () => {
  const context = useContext(ServerConfigContext);
  if (!context) {
    throw new Error('useServerConfig must be used within a ServerConfigProvider');
  }
  return context;
};